import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { Link } from "@tanstack/react-router";
import * as React from "react";

export type BreadcrumbItemType = {
  label: string;
  to?: string;
  params?: Record<string, string>;
};

export type BreadcrumbProps = {
  items: BreadcrumbItemType[];
  className?: string;
};

type VisibleItemsResult =
  | {
      visible: BreadcrumbItemType[];
      hidden: [];
      showEllipsis: false;
    }
  | {
      visible: { sm: BreadcrumbItemType[]; md: BreadcrumbItemType[] };
      hidden: { sm: BreadcrumbItemType[]; md: BreadcrumbItemType[] };
      showEllipsis: true;
    };

function getVisibleItems(items: BreadcrumbItemType[]): VisibleItemsResult {
  const totalItems = items.length;

  if (totalItems <= 2) {
    // sm และ md: ถ้ามี ≤ 2 items แสดงทั้งหมด
    return { visible: items, hidden: [], showEllipsis: false };
  }

  // sm: แสดง 2 items สุดท้าย
  // md: แสดง 3 items สุดท้าย
  const smVisibleCount = 1;
  const mdVisibleCount = 3;

  if (totalItems <= smVisibleCount) {
    return { visible: items, hidden: [], showEllipsis: false };
  }

  // สำหรับ sm: hidden = items[0..totalItems-2-1]
  // สำหรับ md: hidden = items[0..totalItems-3-1]
  const smHidden = items.slice(0, totalItems - smVisibleCount);
  const mdHidden = items.slice(0, totalItems - mdVisibleCount);

  const smVisible = items.slice(-smVisibleCount);
  const mdVisible = items.slice(-mdVisibleCount);

  return {
    visible: { sm: smVisible, md: mdVisible },
    hidden: { sm: smHidden, md: mdHidden },
    showEllipsis: true,
  };
}

function EllipsisDropdown({
  hiddenItems,
}: {
  hiddenItems: BreadcrumbItemType[];
}) {
  if (hiddenItems.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1">
        <BreadcrumbEllipsis className="size-4" />
        <span className="sr-only">Toggle menu</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {hiddenItems.map((item, index) => {
          const key = `${item.label}-${index}`;
          if (item.to) {
            return (
              <DropdownMenuItem key={key} asChild>
                <Link
                  to={item.to as `${string}`}
                  params={item.params as Record<string, string> | undefined}
                >
                  {item.label}
                </Link>
              </DropdownMenuItem>
            );
          }
          return (
            <DropdownMenuItem key={key} disabled>
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function renderBreadcrumbItems(
  items: BreadcrumbItemType[],
  hiddenItems: BreadcrumbItemType[],
) {
  const hasEllipsis = hiddenItems.length > 0;

  return (
    <>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const key = `${item.label}-${index}`;
        const showEllipsisBefore = hasEllipsis && index === 0;

        return (
          <React.Fragment key={key}>
            {showEllipsisBefore && (
              <>
                <BreadcrumbItem>
                  <EllipsisDropdown hiddenItems={hiddenItems} />
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}

            <BreadcrumbItem>
              {isLast ? (
                <BreadcrumbPage className="max-w-[200px] truncate sm:max-w-none">
                  {item.label}
                </BreadcrumbPage>
              ) : item.to ? (
                <BreadcrumbLink asChild>
                  <Link
                    to={item.to as `${string}`}
                    params={item.params as Record<string, string> | undefined}
                    className="max-w-[200px] truncate sm:max-w-none"
                  >
                    {item.label}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <span className="max-w-[200px] truncate text-foreground sm:max-w-none">
                  {item.label}
                </span>
              )}
            </BreadcrumbItem>

            {!isLast && <BreadcrumbSeparator />}
          </React.Fragment>
        );
      })}
    </>
  );
}

export function BreadcrumbComponent({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null;

  const result = getVisibleItems(items);

  // ถ้าไม่มี ellipsis แสดงแบบเดิม
  if (!result.showEllipsis) {
    const visibleItems = result.visible;

    return (
      <Breadcrumb className={cn("flex-1", className)}>
        <BreadcrumbList>
          {visibleItems.map((item, index) => {
            const isLast = index === visibleItems.length - 1;
            const key = `${item.label}-${index}`;

            return (
              <React.Fragment key={key}>
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage className="max-w-[200px] truncate sm:max-w-none">
                      {item.label}
                    </BreadcrumbPage>
                  ) : item.to ? (
                    <BreadcrumbLink asChild>
                      <Link
                        to={item.to as `${string}`}
                        params={
                          item.params as Record<string, string> | undefined
                        }
                        className="max-w-[200px] truncate sm:max-w-none"
                      >
                        {item.label}
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <span className="max-w-[200px] truncate text-foreground sm:max-w-none">
                      {item.label}
                    </span>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // มี ellipsis - แสดงแบบ responsive
  const { sm: smHidden, md: mdHidden } = result.hidden;
  const { sm: smVisible, md: mdVisible } = result.visible;

  return (
    <Breadcrumb className={cn("flex-1", className)}>
      {/* sm: แสดง ellipsis กับ 2 items สุดท้าย */}
      <BreadcrumbList className="flex sm:hidden">
        {renderBreadcrumbItems(smVisible, smHidden)}
      </BreadcrumbList>

      {/* md: แสดง ellipsis กับ 2 items สุดท้าย (ซ้ำกับ sm แต่ใช้ responsive class) */}
      <BreadcrumbList className="hidden sm:flex md:hidden">
        {renderBreadcrumbItems(smVisible, smHidden)}
      </BreadcrumbList>

      {/* md+: แสดง ellipsis กับ 3 items สุดท้าย */}
      <BreadcrumbList className="hidden md:flex">
        {renderBreadcrumbItems(mdVisible, mdHidden)}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

// Export as Breadcrumb to maintain backward compatibility
export { BreadcrumbComponent as Breadcrumb };
